import {
  clearChatbotChatId,
  readChatbotChatId,
  writeChatbotChatId,
} from '../persistence/chatbotChatIdStorage';
import { createAiChatHttpClient } from '../api/httpClient';
import { chatbotChatIdPath } from '../api/paths';
import { getAiChatApiBase, getAiChatWebSocketUrl, isChatbotWebSocketEnabled } from '../config';
import { ChatbotWebSocketSession } from './chatbotWebSocketSession';

/** @type {{ chatId: string } | null} */
let chatIdCache = null;
/** @type {Promise<{ chatId: string }> | null} */
let chatIdInflight = null;
/** @type {'memory' | 'storage' | 'network' | null} */
let lastChatIdSource = null;

/** @type {ChatbotWebSocketSession | null} */
let session = null;

/** @type {Promise<void> | null} */
let connectInflight = null;

function clearChatIdCache() {
  chatIdCache = null;
  chatIdInflight = null;
  lastChatIdSource = null;
  clearChatbotChatId();
}

function hydrateChatIdFromStorage() {
  const stored = readChatbotChatId();
  if (!stored) {
    return null;
  }
  const result = { chatId: stored };
  chatIdCache = result;
  lastChatIdSource = 'storage';
  return result;
}

async function fetchChatId() {
  if (!getAiChatApiBase()) {
    throw new Error('AI chat API base is not configured.');
  }
  const client = createAiChatHttpClient();
  if (!client) {
    throw new Error('AI chat HTTP client is not available.');
  }
  const { data } = await client.get(chatbotChatIdPath());
  const chatId =
    data && typeof data === 'object' && typeof data.chatId === 'string'
      ? data.chatId.trim()
      : '';
  if (!chatId) {
    throw new Error('Chatbot chat-id response did not include a valid chatId.');
  }
  writeChatbotChatId(chatId);
  lastChatIdSource = 'network';
  return { chatId };
}

function fetchChatIdForSession() {
  if (chatIdCache) {
    if (!lastChatIdSource) {
      lastChatIdSource = 'memory';
    }
    return Promise.resolve(chatIdCache);
  }
  const stored = hydrateChatIdFromStorage();
  if (stored) {
    return Promise.resolve(stored);
  }
  if (!chatIdInflight) {
    chatIdInflight = fetchChatId()
      .then((result) => {
        chatIdCache = result;
        return result;
      })
      .finally(() => {
        chatIdInflight = null;
      });
  }
  return chatIdInflight;
}

function buildWsUrl(baseUrl, chatId) {
  const trimmed = (baseUrl || '').trim();
  if (!trimmed) {
    return '';
  }
  let u;
  try {
    u = new URL(trimmed);
  } catch {
    return trimmed;
  }
  const id = (chatId || '').trim();
  if (id) {
    u.searchParams.set('chatId', id);
  }
  return u.toString();
}

async function resolveWsUrl() {
  const { chatId } = await fetchChatIdForSession();
  return buildWsUrl(getAiChatWebSocketUrl(), chatId);
}

export function getChatbotWsSession() {
  if (session && !session.closedByUser) {
    return session;
  }
  if (session) {
    session = null;
  }
  session = new ChatbotWebSocketSession(() => resolveWsUrl());
  return session;
}

function closeSession() {
  if (!session) {
    return;
  }
  const current = session;
  session = null;
  connectInflight = null;
  current.close();
}

async function openChatbotWsOnce() {
  await getChatbotWsSession().ensureOpen();
}

async function connectChatbotWsInternal() {
  const usedStoredChatId = lastChatIdSource === 'storage';
  try {
    await openChatbotWsOnce();
  } catch (err) {
    if (!usedStoredChatId) {
      throw err;
    }
    clearChatIdCache();
    closeSession();
    await fetchChatIdForSession();
    await openChatbotWsOnce();
    return;
  }
  if (usedStoredChatId) {
    lastChatIdSource = 'memory';
  }
}

export function connectChatbotWs() {
  if (!isChatbotWebSocketEnabled()) {
    return Promise.resolve();
  }
  if (connectInflight) {
    return connectInflight;
  }
  const promise = connectChatbotWsInternal().finally(() => {
    if (connectInflight === promise) {
      connectInflight = null;
    }
  });
  connectInflight = promise;
  return promise;
}

/** Reconnect WebSocket for a new chat thread; reuses the session chat-id ticket. */
export function refreshChatbotWs() {
  closeSession();
  return connectChatbotWs();
}

/** Clear session chat-id and WebSocket (e.g. on logout). */
export function resetChatbotWsSession() {
  clearChatIdCache();
  closeSession();
}
